import React, {
  ReactElement,
  useMemo,
  useState,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import { useQueryClient } from 'react-query';
import dayjs from 'dayjs';
import { Flex, Text, Heading } from 'rebass/styled-components';

import useTelephonyLogColumns from './useTelephonyLogColumns';
import { AboutTabs } from './aboutTab';
import { Improvements } from './styles';

import {
  MainContent,
  Card,
  Loader,
  EmptyState,
  Table,
  PageContent,
} from 'ui/patterns';
import { Separator, Button } from 'ui/components';

import NoDataImage from 'assets/images/NoDataImage';
import Icons from 'assets/icons';

import { useDocumentTitle, useGetReleaseNotes, useGetTwilioLogs } from 'hooks';

const sortItem = { sortKey: 'Updated At', sortOrder: 'desc' };

export default function About(): ReactElement {
  useDocumentTitle('About', true);

  const queryClient = useQueryClient();
  const tbodyRef = useRef() as React.MutableRefObject<HTMLTableSectionElement>;

  const columns = useTelephonyLogColumns();

  const [columnsSearch, setColumnsSearch] = useState({}); // header search
  const [currentSortItem, setCurrentSortItem] = useState(sortItem);
  const [lastUpdated, setLastUpdated] = useState<string>();

  //const { data: releaseNotes, isLoading } = useGetReleaseNotes();
  const {
    data: telephonyData,
    isLoading: isTelephonyLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
  } = useGetTwilioLogs({
    columnsSearch,
    currentSortItem,
    setLastUpdated,
  });

  let permissions = [];
  const permissionSet = localStorage.getItem('userPermissions');
  if (permissionSet) {
    permissions = JSON.parse(permissionSet);
  }

  const tableData = useMemo(() => {
    if (telephonyData) {
      return telephonyData?.pages?.map((dataItem: any) => dataItem.Data).flat();
    }
  }, [telephonyData]);

  const handleScroll = useCallback(
    (e: any) => {
      if (tbodyRef.current === e.target) {
        const bottom =
          e.target.clientHeight - 10 <
          e.target.scrollHeight - e.target.scrollTop &&
          e.target.scrollHeight - e.target.scrollTop <
          e.target.clientHeight + 10;
        if (bottom) {
          hasNextPage && fetchNextPage();
        }
      }
    },
    [fetchNextPage, hasNextPage],
  );

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [handleScroll]);

  const totalItems = useMemo(() => {
    if (telephonyData) {
      return telephonyData.pages[0].TotalRecords;
    }
    return 0;
  }, [telephonyData]);

  const onEndReached = () => {
    if (tableData) {
      if (hasNextPage && tableData?.length < totalItems) fetchNextPage();
    }
  };

  const headerUpdateCallBack = useCallback(
    ({ columnId, value }: { columnId: string; value: string }) => {
      setColumnsSearch({
        ...columnsSearch,
        [columnId]: value,
      });
    },
    [columnsSearch],
  );
  const handleSortChange = useCallback(
    (item: string): void => {
      let sortOrder = 'asc';
      if (
        currentSortItem.sortKey === item &&
        currentSortItem.sortOrder === 'asc'
      ) {
        sortOrder = 'desc';
      }
      setCurrentSortItem({
        sortKey: item,
        sortOrder,
      });
    },
    [setCurrentSortItem, currentSortItem],
  );

  //reset filters
  function resetFilters() {
    queryClient.removeQueries([
      'telephony_list',
      columnsSearch,
      currentSortItem,
    ]);
    setCurrentSortItem(sortItem);
    setColumnsSearch({});
    refetch();
  }

  //refresh list
  function refetchList() {
    queryClient.removeQueries([
      'telephony_list',
      columnsSearch,
      currentSortItem,
    ]);
    refetch();
  }

  function renderReleaseNotes<ReactElement>() {
    return (
      <>
        {/* {!isLoading && releaseNotes && releaseNotes?.length !== 0 ? (
          <Card p="5" minWidth="350px" maxWidth="500px">
            {releaseNotes &&
              releaseNotes.length > 0 &&
              releaseNotes?.map((releaseItem, releaseIndex) => (
                <>
                  <Heading as="h2" fontWeight="bold" mb={2} color="text.body">
                    {releaseItem.Version}
                    <Text
                      as="small"
                      fontSize={2}
                      fontWeight="medium"
                      color="var(--color-neutral-contrast-05)"
                      ml={2}
                    >
                      {`Released on ${dayjs(releaseItem.ReleasedOn).format(
                        'MM/DD/YYYY',
                      )}`}
                    </Text>
                  </Heading>
                  <Improvements>
                    {releaseItem?.ReleaseNotes &&
                      releaseItem?.ReleaseNotes.length > 0 &&
                      releaseItem?.ReleaseNotes?.map((noteItem, noteIndex) => (
                        <li key={noteIndex}>{noteItem}</li>
                      ))}
                  </Improvements>
                  {releaseIndex !== releaseNotes?.length - 1 ? (
                    <Separator direction="x" my={6} />
                  ) : null}
                </>
              ))}
          </Card>
        ) : null} */}

        {/* {isLoading && <Loader />} */}
        {/* {!isLoading && (!releaseNotes || releaseNotes.length === 0) && ( */}
        <EmptyState>
          <NoDataImage feedback="No data available" />
        </EmptyState>
        {/* )} */}
      </>
    );
  }

  function telephonyLogGrid<ReactElement>() {
    return (
      <Flex flexDirection="column" height="100%">
        <Flex
          pb={2}
          justifyContent="space-between"
          alignItems={'baseline'}
          style={{ gap: '1ch' }}
        >
          <Flex style={{}} alignItems="baseline">
            {lastUpdated !== undefined && (
              <p>
                Log last refreshed at <strong>{`${lastUpdated}`}</strong> Please
                Refresh for the latest changes.
              </p>
            )}
            <Button variant="link" onClick={() => refetchList()}>
              Refresh list
            </Button>
          </Flex>
          <Button
            variant="secondary"
            iconBefore={<Icons.Refresh />}
            onClick={() => resetFilters()}
          >
            Reset filters
          </Button>
        </Flex>
        <Table
          columns={columns}
          data={tableData ? tableData : []}
          headerUpdateCallBack={headerUpdateCallBack}
          handleSortChange={handleSortChange}
          sortedItem={currentSortItem}
          columnsSearch={columnsSearch}
          isLoading={isTelephonyLoading}
          isFetching={!!isFetchingNextPage}
          onEndReached={onEndReached}
          noOptions
          hasInlineLoader
        />
      </Flex>
    );
  }

  const tabContentsForFullPermission = [
    {
      id: '1',
      label: 'Release Notes',
      content: renderReleaseNotes(),
      defaultSelected: true,
    },
    ...(process.env.REACT_APP_THEME?.toLocaleLowerCase() === 'protocall'
      ? [
          {
            id: '2',
            label: 'Telephony Log',
            content: telephonyLogGrid(),
            defaultSelected: false,
          },
        ]
      : []),
  ];

  const tabContents = [
    {
      id: '1',
      label: 'Release Notes',
      content: renderReleaseNotes(),
      defaultSelected: true,
    },
  ];

  const procedureName = process.env.REACT_APP_THEME;

  return (
    <MainContent flexDirection="column">
      <PageContent>
        <AboutTabs
          tabs={
            procedureName !== 'lfl' && permissions?.isTelephonyAdmin
              ? tabContentsForFullPermission
              : tabContents
          }
        />
      </PageContent>
    </MainContent>
  );
}
